import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import Cookies from 'universal-cookie';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { faStar } from '@fortawesome/pro-solid-svg-icons/faStar';
import { faHeart } from '@fortawesome/pro-solid-svg-icons/faHeart';
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay';
import { faClipboardList } from '@fortawesome/pro-solid-svg-icons/faClipboardList';
import { faEllipsis } from '@fortawesome/pro-solid-svg-icons/faEllipsis';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import { faComment } from '@fortawesome/pro-solid-svg-icons/faComment';
import { faLock } from '@fortawesome/pro-solid-svg-icons/faLock';
import { faPaperclip } from '@fortawesome/pro-solid-svg-icons/faPaperclip';
import { faHeart as faHeartRegular } from '@fortawesome/pro-regular-svg-icons/faHeart';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FamilyPaywallModal } from 'us-react-components/dist/FamilyPaywallModal/FamilyPaywallModal';
import { FlagProfileModal } from 'us-react-components/dist/FlagProfileModal/FlagProfileModal';
import { InterestWarningModal } from 'us-react-components/dist/InterestWarningModal/InterestWarningModal';
import { ProfileVideoModal } from 'us-react-components/dist/ProfileVideoModal/ProfileVideoModal';
import { SitterNoteModal } from 'us-react-components/dist/SitterNoteModal/SitterNoteModal';

import {
  FavoriteSitterService,
  InterestedSitter,
  InterestedSitterService,
  Job,
  JobHiddenSitterService,
  JobService,
  Sitter,
  SitterService,
  getApiErrorMessage,
} from 'us-web-services';
import { getShortDateString } from 'us-react-components/dist/utils/date';
import { fullName } from 'us-react-components/dist/utils/string';

import { Button, ButtonType } from '../../components';
import { Storage } from '../../constants';
import { useDependencies } from '../../dependencies';
import { ModalName } from '../../modals/constants';
import { useModal } from '../../modals/modalContext';
import { checkForPaywall } from '../../utils/PaywallUtil';
import { DisplayUtil } from '../../utils/DisplayUtil';


export const ApplicantSummary = observer((): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: jobId, sitterId } = useParams<{ id: string; sitterId: string }>();
  const { stores, services: { tracker, config } } = useDependencies();
  const modal = useModal();
  const openIdentity = (): void => modal?.setModalName(ModalName.IdentityVerification);
  const user = stores.user.user;
  const cookies = new Cookies();

  const [job, setJob] = useState<Job | null>(null);
  const [sitter, setSitter] = useState<Sitter | null>(null);
  const [interest, setInterest] = useState<InterestedSitter | null>(null);
  const [applicants, setApplicants] = useState<InterestedSitter[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [hiding, setHiding] = useState(false);
  const [playingVimeoId, setPlayingVimeoId] = useState<string | null>(null);

  const [noteOpen, setNoteOpen] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (): void => setMenuOpen(false);
    document.addEventListener('click', close);
    return (): void => document.removeEventListener('click', close);
  }, [menuOpen]);

  useEffect(() => {
    document.title = 'UrbanSitter - Applicant Summary';
  }, []);

  useEffect(() => {
    if (!jobId || !sitterId || !user?.id) return;
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const [jobResp, sitterResp, interestResp, listResp] = await Promise.all([
          JobService.findById(Number(jobId), { params: { userId: user.id } }),
          SitterService.get(Number(sitterId), {
            params: { userId: user.id, parentId: user.id },
          }),
          InterestedSitterService.getByFilter({
            params: { jobId: Number(jobId), sitterId: Number(sitterId), userId: user.id, parentId: user.id },
          }),
          InterestedSitterService.getByFilter({
            params: { jobId: Number(jobId), userId: user.id, parentId: user.id, withdrawn: false },
          }),
        ]);
        if (cancelled) return;
        setJob((jobResp.data?.data ?? null) as Job | null);
        setSitter((sitterResp.data?.data ?? null) as Sitter | null);
        setInterest(((interestResp.data?.data ?? [])[0] ?? null) as InterestedSitter | null);
        setApplicants((listResp.data?.data ?? []) as InterestedSitter[]);
      } catch (err) {
        console.warn('[ApplicantSummary] load failed', err);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };
    void load();
    return (): void => {
      cancelled = true;
    };
  }, [jobId, sitterId, user?.id]);

  const [interestWarningOpen, setInterestWarningOpen] = useState(false);

  const trackEvent = (action: string): void => {
    tracker.track('clicked-button', { action, component: 'Applicant Summary' });
  };

  const setOriginCookie = (): void => {
    cookies.set(
      Storage.BOOKING_ORIGIN,
      `/app${window.location.pathname}${window.location.search}`,
      { path: '/' },
    );
  };

  // Mirrors applicant-summary.js:103 — enters the booking wizard at the
  // selection step (vertical picker / open job picker), not select-date.
  const proceedToBook = (): void => {
    setOriginCookie();
    navigate(`/parent/bookings/select?sitterId=${sitterId}&jobId=${jobId}`);
  };

  const book = (): void => {
    trackEvent('Book CTA');
    // Warning first, then verification gate.
    const warning = (interest as unknown as { warning?: boolean } | null)?.warning;
    if (warning) {
      setInterestWarningOpen(true);
      return;
    }
    stores.verification.checkVerification(proceedToBook, openIdentity);
  };
  const interview = (): void => {
    trackEvent('Interview CTA');
    stores.verification.checkVerification(() => {
      setOriginCookie();
      navigate(`/parent/interviews/select?sitterId=${sitterId}`);
    }, openIdentity);
  };
  const message = (): void => {
    trackEvent('Message CTA');
    stores.verification.checkVerification(() => {
      if (!sitter) return;
      checkForPaywall({
        userId: user?.id,
        sitter,
        action: (): void => {
          navigate(`/parent/${user?.id}/messages/${sitterId}`);
        },
        openPaywall: (): void => {
          setPaywallOpen(true);
        },
      });
    }, openIdentity);
  };

  const toggleHide = async (): Promise<void> => {
    if (!interest || hiding) return;
    const hideSitterId = interest.sitter?.id;
    const hideJobId = interest.job?.id ?? job?.id;
    if (!hideSitterId || !hideJobId) return;
    setHiding(true);
    try {
      const next = !interest.hidden;
      if (next) {
        const resp = await JobHiddenSitterService.hide(hideJobId, hideSitterId);
        const created = (resp.data?.data ?? {}) as { id?: number };
        setInterest((prev) =>
          prev ? { ...prev, hidden: true, hiddenSitterId: created.id } : prev,
        );
      } else if (interest.hiddenSitterId) {
        await JobHiddenSitterService.unhide(interest.hiddenSitterId);
        setInterest((prev) =>
          prev ? { ...prev, hidden: false, hiddenSitterId: undefined } : prev,
        );
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error) ?? t('pages.applicantSummary.couldntUpdate'));
    } finally {
      setHiding(false);
    }
  };

  const toggleFavorite = async (): Promise<void> => {
    if (!sitter?.id || !user?.id) return;
    try {
      if (sitter.relationship?.bookmarked && sitter.relationship.bookmarkedId) {
        await FavoriteSitterService.delete(sitter.relationship.bookmarkedId);
        setSitter((prev) =>
          prev
            ? {
                ...prev,
                relationship: {
                  ...(prev.relationship ?? {}),
                  bookmarked: false,
                  bookmarkedId: undefined,
                },
              }
            : prev,
        );
      } else {
        const resp = await FavoriteSitterService.create({
          sitterId: sitter.id,
          parentId: user.id,
        } as never);
        const created = resp.data?.data as { id?: number } | undefined;
        setSitter((prev) =>
          prev
            ? {
                ...prev,
                relationship: {
                  ...(prev.relationship ?? {}),
                  bookmarked: true,
                  bookmarkedId: created?.id,
                },
              }
            : prev,
        );
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error) ?? t('pages.applicantSummary.couldntUpdate'));
    }
  };

  const currentIdx = applicants.findIndex(
    (a) => String((a.sitter as { id?: number } | undefined)?.id) === sitterId,
  );
  const hasPrev = currentIdx > 0;
  const hasNext = currentIdx >= 0 && currentIdx < applicants.length - 1;
  const goToPrev = (): void => {
    if (!hasPrev) return;
    const next = applicants[currentIdx - 1];
    const id = (next?.sitter as { id?: number } | undefined)?.id;
    if (id) navigate(`/job/${jobId}/applicant/${id}`);
  };
  const goToNext = (): void => {
    if (!hasNext) return;
    const next = applicants[currentIdx + 1];
    const id = (next?.sitter as { id?: number } | undefined)?.id;
    if (id) navigate(`/job/${jobId}/applicant/${id}`);
  };

  const onNoteSaved = (note?: { id?: number; comment?: string }): void => {
    setSitter((prev) =>
      prev
        ? {
            ...prev,
            relationship: { ...(prev.relationship ?? {}), parentNote: note },
          }
        : prev,
    );
  };

  if (!loaded) {
    return (
      <div className='container py-8 text-center'>
        <i className='fa-solid fa-spinner fa-spin mr-2' />
        <span>{t('common.loading')}</span>
      </div>
    );
  }

  if (!sitter || !interest) {
    return <div className='container py-8' />;
  }

  const stars = Math.round(sitter.rating ?? 0);
  const rateToShow = job?.booking?.rate
    ? null
    : sitter.displayRate?.minimumRate
      ? `$${sitter.displayRate.minimumRate}/hr`
      : null;

  return (
    <div className='container py-6'>
      <h2 className='text-2xl font-semibold mb-4'>{t('pages.applicantSummary.heading')}</h2>

      <div className='grid grid-cols-1 md:grid-cols-12 gap-6'>
        <div className='md:col-span-8 us-card rounded p-5'>
          <div className='flex items-start justify-between mb-4'>
            <div>
              {sitter.vimeoId && (
                <Button
                  buttonType={ButtonType.secondary}
                  id={`play-video-${sitter.id}`}
                  className='btn btn-secondary'
                  onClick={(): void => setPlayingVimeoId(sitter.vimeoId ?? null)}
                >
                  <FontAwesomeIcon icon={faPlay} className='align-middle mr-2' /> Video
                </Button>
              )}
              {sitter.relationship?.distance && (
                <div className='mt-2 text-sm text-neutral-600'>
                  {DisplayUtil.getDistanceText(t, sitter.relationship.distance)}
                </div>
              )}
            </div>
            <div className='text-center flex-1'>
              <div className='relative inline-block'>
                <a href={`/app/sitter/${sitter.id}`}>
                  <img
                    src={sitter.pictureUrl}
                    alt={fullName(sitter)}
                    className='w-24 h-24 rounded-full object-cover mx-auto'
                  />
                </a>
                <Button
                  buttonType={ButtonType.tertiary}
                  id={`favorite-${sitter.id}`}
                  aria-label={
                    sitter.relationship?.bookmarked
                      ? 'Remove from favorites'
                      : 'Add to favorites'
                  }
                  onClick={toggleFavorite}
                  className={`absolute bottom-0 right-0 w-9 h-9 inline-flex items-center justify-center bg-white rounded-full border border-neutral-300 shadow !p-0 ${
                    sitter.relationship?.bookmarked ? 'text-sapphire' : 'text-neutral-400'
                  }`}
                >
                  <FontAwesomeIcon
                    icon={sitter.relationship?.bookmarked ? faHeart : faHeartRegular}
                    className='text-lg leading-none'
                  />
                </Button>
              </div>
              <h1 className='font-semibold mt-3 mb-1'>{sitter.name}</h1>
              {sitter.repeatFamilies ? (
                <div className='text-sm'>
                  Booked by {sitter.repeatFamilies} repeat famil
                  {sitter.repeatFamilies === 1 ? 'y' : 'ies'}
                </div>
              ) : null}
              {rateToShow && <div className='text-sm'>{rateToShow}</div>}
              {sitter.rating != null && sitter.rating > 0 && (
                <div className='flex justify-center gap-0.5 mt-1 items-center'>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <FontAwesomeIcon
                      key={n}
                      icon={faStar}
                      className={n <= stars ? 'text-honey' : 'text-gray-100'}
                    />
                  ))}
                  {!!sitter.reviewCount && (
                    <span className='text-sm text-neutral-600 ml-1'>
                      ({sitter.reviewCount})
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className='flex flex-col gap-2 w-32 relative'>
              <div className='flex justify-end mb-1'>
                <Button
                  id='applicant-options'
                  buttonType={ButtonType.tertiary}
                  aria-label='More options'
                  className='!p-1'
                  onClick={(e): void => {
                    e.stopPropagation();
                    setMenuOpen((v) => !v);
                  }}
                >
                  <FontAwesomeIcon icon={faEllipsis} className='text-xl' />
                </Button>
              </div>
              {menuOpen && (
                <ul
                  className='absolute right-0 top-8 z-10 bg-white shadow rounded border border-neutral-200 py-1 w-56 text-sm'
                  onClick={(e): void => e.stopPropagation()}
                  onKeyDown={(e): void => e.stopPropagation()}
                  role='menu'
                >
                  <li>
                    <a
                      id={`dropdown-sitter-${sitter.id}`}
                      href={`/app/sitter/${sitter.id}`}
                      className='block px-4 py-2 hover:bg-neutral-100'
                    >
                      View Full Profile
                    </a>
                  </li>
                  <li>
                    <a
                      id={`job-post-${jobId}`}
                      href={`/app/job/${jobId}`}
                      className='block px-4 py-2 hover:bg-neutral-100'
                    >
                      View Post Details
                    </a>
                  </li>
                  <li>
                    <button
                      id={`add-note-${sitter.id}`}
                      type='button'
                      className='block w-full text-left px-4 py-2 hover:bg-neutral-100'
                      onClick={(): void => {
                        setMenuOpen(false);
                        setNoteOpen(true);
                      }}
                    >
                      {sitter.relationship?.parentNote
                        ? t('pages.applicantSummary.editNote')
                        : t('pages.applicantSummary.addPrivateNote')}
                    </button>
                  </li>
                  <li>
                    <button
                      id={`flag-profile-${sitter.id}`}
                      type='button'
                      className='block w-full text-left px-4 py-2 hover:bg-neutral-100 text-red-600'
                      onClick={(): void => {
                        setMenuOpen(false);
                        setFlagOpen(true);
                      }}
                    >
                      {t('pages.applicantSummary.flagProfile')}
                    </button>
                  </li>
                </ul>
              )}
              <Button buttonType={ButtonType.primary} onClick={book}>
                Book
              </Button>
              <Button buttonType={ButtonType.secondary} onClick={message}>
                Message
              </Button>
              {!interest.hidden && (
                <Button buttonType={ButtonType.secondary} onClick={interview}>
                  Interview
                </Button>
              )}
              <Button
                buttonType={ButtonType.secondary}
                onClick={toggleHide}
                disabled={hiding}
              >
                {interest.hidden ? t('pages.applicantSummary.unpass') : t('pages.applicantSummary.pass')}
              </Button>
            </div>
          </div>

          <hr className='my-4' />
          <div className='text-center italic text-sm'>
            {sitter.firstName} applied {interest.dateInterestedLabel ?? getShortDateString(interest.dateInterested)}
          </div>
          <hr className='my-4' />

          <div className='border border-neutral-200 rounded p-4 mb-4'>
            <h2 className='font-semibold mb-2'>
              <FontAwesomeIcon icon={faPaperclip} className='mr-2' />
              From {sitter.firstName}
            </h2>
            <p className='whitespace-pre-line break-words'>
              {interest.message
                ? interest.message
                : `${sitter.firstName} did not send a personal introduction.`}
            </p>
          </div>

          {job?.questions && job.questions.length > 0 && (
            <div className='border border-neutral-200 rounded p-4 mb-4'>
              <p className='italic mb-2'>Screening Question Responses</p>
              {!interest.answers?.length && (
                <p>
                  {sitter.firstName} did not respond to your screening questions.
                </p>
              )}
              {interest.answers?.map((answer, i) => (
                <div key={i} className='mb-2'>
                  <i className='block text-sm'>{answer.question?.text}</i>
                  <div className='break-words'>{answer.text}</div>
                </div>
              ))}
            </div>
          )}

          <div className='border border-neutral-200 rounded p-4 mb-4'>
            <h2 className='font-semibold mb-2'>About {sitter.firstName}</h2>
            {sitter.about ? (
              <div className='whitespace-pre-line break-words'>{sitter.about}</div>
            ) : (
              <p className='text-neutral-500'>
                {sitter.firstName} hasn&apos;t written a bio yet.
              </p>
            )}
          </div>

          {(sitter.availabilityStatus?.description ||
            sitter.relationship?.bookingCount ||
            sitter.relationship?.lastMessaged ||
            sitter.relationship?.positiveReview ||
            sitter.relationship?.parentNote) && (
            <div className='border border-neutral-200 rounded p-4 mb-4'>
              <p className='italic mb-2'>More about {sitter.firstName}</p>
              {sitter.availabilityStatus?.description && (
                <div className='mb-2'>
                  {sitter.jobPreferences && sitter.jobPreferences.length > 0 && (
                    <h3 className='font-semibold'>
                      Looking for{' '}
                      {sitter.jobPreferences.map((p) => p.name).filter(Boolean).join(', ')}
                    </h3>
                  )}
                  <p className='break-words'>{sitter.availabilityStatus.description}</p>
                </div>
              )}
              {sitter.relationship?.bookingCount ? (
                <div className='mb-1'>
                  <FontAwesomeIcon icon={faCircleCheck} className='mr-2 text-sapphire' />
                  {sitter.relationship.bookingCount} booking
                  {sitter.relationship.bookingCount === 1 ? '' : 's'} with you
                </div>
              ) : null}
              {sitter.relationship?.lastMessaged && (
                <div className='mb-1'>
                  <FontAwesomeIcon icon={faComment} className='mr-2 text-sapphire' />
                  Last message {getShortDateString(sitter.relationship.lastMessaged)}
                </div>
              )}
              {sitter.relationship?.positiveReview && (
                <div className='mb-1'>
                  <FontAwesomeIcon icon={faStar} className='mr-2 text-honey' />
                  Positive review from you
                </div>
              )}
              {sitter.relationship?.parentNote && (
                <div className='mb-1'>
                  <FontAwesomeIcon icon={faLock} className='mr-2 text-sapphire' />
                  1 note
                </div>
              )}
            </div>
          )}

        </div>

        {/* Right-column: interested caregivers + quick links */}
        <aside className='md:col-span-4 hidden md:block space-y-4'>
          <div className='us-card rounded p-4 text-center'>
            <FontAwesomeIcon icon={faClipboardList} className='text-3xl mb-2 text-sapphire' aria-hidden='true' />
            <h2 className='font-semibold mb-2'>
              {applicants.length} interested caregiver
              {applicants.length === 1 ? '' : 's'}
            </h2>
            <div className='flex justify-center gap-2 mb-3 flex-wrap'>
              {applicants.slice(0, 5).map((a) => (
                <img
                  key={(a.sitter as { id?: number } | undefined)?.id ?? Math.random()}
                  className='w-10 h-10 rounded-full'
                  src={(a.sitter as { pictureUrl?: string } | undefined)?.pictureUrl}
                  alt=''
                />
              ))}
            </div>
            <div className='space-y-2'>
              <a
                id='go-to-full-list'
                href={`/app/job/${jobId}/interest`}
                className='btn btn-secondary btn-block w-full'
              >
                See Full List
              </a>
              {hasPrev && (
                <Button
                  id='go-to-prev'
                  buttonType={ButtonType.secondary}
                  className='btn btn-secondary btn-block w-full'
                  onClick={goToPrev}
                >
                  Previous
                </Button>
              )}
              {hasNext && (
                <Button
                  id='go-to-next'
                  buttonType={ButtonType.secondary}
                  className='btn btn-secondary btn-block w-full'
                  onClick={goToNext}
                >
                  Next
                </Button>
              )}
            </div>
          </div>
        </aside>
      </div>

      {sitter.id && user?.id && (
        <SitterNoteModal
          isOpen={noteOpen}
          onClose={(): void => setNoteOpen(false)}
          sitterId={sitter.id}
          parentId={user.id}
          initialNote={sitter.relationship?.parentNote}
          onSaved={onNoteSaved}
        />
      )}
      {sitter.id && user?.id && (
        <FlagProfileModal
          isOpen={flagOpen}
          onClose={(): void => setFlagOpen(false)}
          flaggedId={sitter.id}
          flaggingId={user.id}
        />
      )}

      <ProfileVideoModal
        isOpen={!!playingVimeoId}
        onClose={(): void => setPlayingVimeoId(null)}
        vimeoId={playingVimeoId ?? undefined}
        title='Caregiver video'
      />
      <InterestWarningModal
        isOpen={interestWarningOpen}
        onClose={(): void => setInterestWarningOpen(false)}
        caregiverName={sitter.firstName}
        onContinue={(): void => {
          setInterestWarningOpen(false);
          proceedToBook();
        }}
      />
      {user?.id && (
        <FamilyPaywallModal
          showModal={paywallOpen}
          onClose={(): void => setPaywallOpen(false)}
          onSuccess={(): void => {
            setPaywallOpen(false);
            if (sitterId) navigate(`/parent/${user.id}/messages/${sitterId}`);
          }}
          sitter={sitter as unknown as Sitter}
          userId={user.id}
          stripeKey={config.STRIPE_KEY}
          recaptchaKey={config.RECAPTCHA_KEY}
        />
      )}
    </div>
  );
});
